import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Star, ArrowRight, Check, RotateCcw, Share2, Copy, X } from "lucide-react";
import { toast } from "sonner";
import {
  fetchProduct, fetchTimeline, fetchEndorsements,
  saveConfiguration, loadConfiguration, formatUsd,
} from "../lib/api";
import { useCart } from "../context/CartContext";
import { PRODUCT } from "../constants/testIds";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const FAQS = [
  { q: "When will my system arrive?", a: "We aim to deliver 3–5 days before your surgery date. Enter your date at checkout." },
  { q: "Is this medical grade?", a: "Every component is a real premium brand product vetted by board-certified surgeons — Marena, Silagen, Alastin, SkinCeuticals, Augustinus Bader and more." },
  { q: "Can I return it?", a: "Yes — unopened systems can be returned within 14 days. Opened items are non-returnable for hygiene reasons." },
  { q: "Do you ship internationally?", a: "We currently ship to the US, Canada, UK and Australia." },
  { q: "What if my surgery is rescheduled?", a: "Contact us any time and we will help you plan around the new date." },
  { q: "Can I customize my bundle?", a: "Yes — every item below can be swapped with a curated alternative before you add to cart. The price updates live. You can also save a configured bundle to a shareable link so friends and surgeons can review it." },
];

export default function ProductPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const configSlug = params.get("c");

  const [product, setProduct] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [endorsements, setEndorsements] = useState({});
  const [variants, setVariants] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [savingShare, setSavingShare] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchProduct(id);
      if (cancelled) return;
      setProduct(p);
      setVariants({});
      const [tl, endo] = await Promise.all([fetchTimeline(), fetchEndorsements()]);
      if (cancelled) return;
      setTimeline(tl);
      setEndorsements(endo);
      if (configSlug) {
        try {
          const cfg = await loadConfiguration(configSlug);
          if (cfg.product_id === p.id) {
            const parsed = {};
            Object.entries(cfg.variants || {}).forEach(([k, v]) => { parsed[Number(k)] = Number(v); });
            setVariants(parsed);
            toast.success(`Loaded shared configuration · ${cfg.swaps.length} swap${cfg.swaps.length === 1 ? "" : "s"}`);
          }
        } catch { toast.error("Could not load shared configuration"); }
      }
    })();
    return () => { cancelled = true; };
  }, [id, configSlug]);

  const { total, resolvedItems, swaps } = useMemo(() => {
    if (!product) return { total: 0, resolvedItems: [], swaps: [] };
    let delta = 0;
    const resolved = [];
    const sw = [];
    product.included.forEach((item, slotIdx) => {
      const optIdx = variants[slotIdx] ?? 0;
      if (optIdx === 0) {
        resolved.push({ ...item, option_index: 0 });
      } else {
        const alts = item.alternatives || [];
        const a = alts[optIdx - 1];
        if (a) {
          resolved.push({
            brand: a.brand, name: a.name, category: item.category,
            msrp: a.msrp, image: a.image, desc: a.desc, option_index: optIdx,
          });
          delta += a.msrp - item.msrp;
          sw.push({ brand: a.brand, name: a.name });
        } else {
          resolved.push({ ...item, option_index: 0 });
        }
      }
    });
    return { total: product.price + delta, resolvedItems: resolved, swaps: sw };
  }, [product, variants]);

  if (!product) return <div className="max-w-7xl mx-auto px-8 py-24 font-serif text-2xl">Loading…</div>;

  const setSlot = (slotIdx, optIdx) => setVariants((v) => ({ ...v, [slotIdx]: optIdx }));
  const reset = () => setVariants({});
  const bundleMsrp = resolvedItems.reduce((s, r) => s + r.msrp, 0);
  const isCustomized = swaps.length > 0;

  const openShare = async () => {
    setSavingShare(true);
    try {
      const res = await saveConfiguration({ product_id: product.id, variants });
      const url = `${window.location.origin}/products/${product.id}?c=${res.slug}`;
      setShareUrl(url);
      setShareOpen(true);
    } catch { toast.error("Could not save configuration"); }
    finally { setSavingShare(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <div>
      {/* GALLERY + INFO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div data-testid={PRODUCT.gallery} className="relative">
          <div className="aspect-[4/5] overflow-hidden bg-[#FDF3ED]">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-6 left-6 bg-[#FBF6F1] px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase">
            {product.duration_weeks}-Week System
          </div>
          {isCustomized && (
            <div className="absolute top-6 right-6 bg-[#2A1F1F] text-[#B89B66] px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase">
              Customized · {swaps.length} swap{swaps.length > 1 ? "s" : ""}
            </div>
          )}
        </div>

        <div>
          <div className="eyebrow">{product.procedure}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] tracking-tight">
            {product.name}
          </h1>
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? "fill-[#B89B66] text-[#B89B66]" : "text-[#E5CDBB]"}`} />
              ))}
            </div>
            <span className="text-xs text-[#6B5757] font-mono tracking-wider">
              {product.rating} · {product.reviews} reviews
            </span>
          </div>
          <p className="text-[#6B5757] mt-8 leading-relaxed">{product.description}</p>

          <div className="mt-10 flex items-end gap-4">
            <div>
              <div className="eyebrow">Your Price</div>
              <div data-testid={PRODUCT.livePrice} className="font-serif text-4xl mt-1">{formatUsd(total)}</div>
              {isCustomized && total !== product.price && (
                <div className="text-xs text-[#6B5757] mt-1">
                  {total > product.price ? "+" : ""}{formatUsd(total - product.price)} vs base
                </div>
              )}
            </div>
            <div className="text-xs text-[#6B5757] pb-2">Free shipping · Delivered before surgery</div>
          </div>

          <button
            data-testid={PRODUCT.addToCart}
            onClick={() => {
              addItem(product, 1, { variants, price: total, swaps });
              toast.success(isCustomized ? "Customized system added" : "Added to cart");
            }}
            className="btn-primary w-full mt-8 inline-flex items-center justify-center gap-3"
          >
            Add to Cart · {formatUsd(total)} <ArrowRight className="w-4 h-4" />
          </button>

          <button
            data-testid={PRODUCT.saveConfig}
            onClick={openShare}
            disabled={savingShare}
            className="btn-outline w-full mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Share2 className="w-4 h-4" /> {savingShare ? "Saving…" : "Save & Share This System"}
          </button>

          <div className="mt-6 grid grid-cols-3 gap-2 text-[10px] font-mono tracking-widest uppercase text-[#6B5757]">
            <div className="border border-[#F0DFD0] p-3 text-center">Surgeon Approved</div>
            <div className="border border-[#F0DFD0] p-3 text-center">Medical Grade</div>
            <div className="border border-[#F0DFD0] p-3 text-center">30-Day Support</div>
          </div>
        </div>
      </section>

      {/* CONFIGURATOR */}
      <section className="bg-[#FDF3ED] py-24" data-testid="product-configurator">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 max-w-5xl">
            <div>
              <div className="eyebrow">Customize Your System</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
                Six slots. Real brands. <span className="italic">Your call.</span>
              </h2>
              <p className="text-[#6B5757] mt-4 max-w-xl leading-relaxed">
                Each slot below is pre-selected with the item surgeons recommend most. Tap an alternative to swap in a different premium brand — your total updates instantly.
              </p>
            </div>
            <div className="text-right whitespace-nowrap">
              <div className="eyebrow">Bundle MSRP</div>
              <div className="font-serif text-2xl mt-1 line-through text-[#A08D8D]">{formatUsd(bundleMsrp)}</div>
              <div className="font-mono text-xs text-[#8B9E7E] mt-1">
                You save {formatUsd(Math.max(0, bundleMsrp - total))}
              </div>
              {isCustomized && (
                <button
                  onClick={reset}
                  data-testid={PRODUCT.resetConfig}
                  className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F]"
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {product.included.map((item, slotIdx) => {
              const options = [item, ...(item.alternatives || [])];
              const currentIdx = variants[slotIdx] ?? 0;
              const chosen = options[currentIdx];
              const hasAlternatives = options.length > 1;
              const chosenEndorsement = endorsements[chosen.brand];
              return (
                <div
                  key={slotIdx}
                  data-testid={PRODUCT.includedItem(slotIdx)}
                  className="bg-white border border-[#F0DFD0] overflow-hidden"
                >
                  <div className="flex gap-5 p-5">
                    <div className="w-28 h-32 flex-shrink-0 overflow-hidden bg-[#FDF3ED]">
                      <img src={chosen.image} alt={chosen.name} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#B85C56]">
                        Slot 0{slotIdx + 1} · {item.category}
                      </div>
                      <h3 className="font-serif text-lg mt-2 leading-tight text-[#2A1F1F]">{chosen.name}</h3>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-medium text-[#2A1F1F]">{chosen.brand}</span>
                        <span className="font-mono text-xs text-[#6B5757]">{formatUsd(chosen.msrp)}</span>
                      </div>
                      <p className="text-xs text-[#6B5757] mt-3 leading-relaxed line-clamp-2">{chosen.desc}</p>
                    </div>
                  </div>

                  {chosenEndorsement && (
                    <div className="px-5 pb-4 -mt-1">
                      <div className="border-l-2 border-[#B89B66] pl-3 py-1">
                        <p className="text-[11px] italic text-[#6B5757] leading-snug">"{chosenEndorsement.quote}"</p>
                        <div className="font-mono text-[9px] tracking-widest uppercase text-[#B85C56] mt-1">
                          — {chosenEndorsement.author}
                        </div>
                      </div>
                    </div>
                  )}

                  {hasAlternatives ? (
                    <div className="border-t border-[#F0DFD0] px-5 py-4 bg-[#FBF6F1]">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-[#6B5757] mb-3">Choose your brand</div>
                      <div className="flex flex-wrap gap-2">
                        {options.map((o, oi) => {
                          const active = oi === currentIdx;
                          const delta = o.msrp - item.msrp;
                          return (
                            <button
                              key={oi}
                              data-testid={PRODUCT.slotOption(slotIdx, oi)}
                              onClick={() => setSlot(slotIdx, oi)}
                              className={`px-3 py-2 text-xs border transition-colors ${
                                active
                                  ? "bg-[#2A1F1F] text-[#FEFBF9] border-[#2A1F1F]"
                                  : "bg-white text-[#2A1F1F] border-[#E5CDBB] hover:border-[#2A1F1F]"
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                {active && <Check className="w-3 h-3" />}
                                <span className="font-medium">{o.brand}</span>
                                {oi !== 0 && (
                                  <span className={`font-mono text-[10px] ${active ? "text-[#B89B66]" : "text-[#6B5757]"}`}>
                                    {delta >= 0 ? "+" : ""}{formatUsd(delta)}
                                  </span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-[#F0DFD0] px-5 py-3 bg-[#FBF6F1]">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-[#A08D8D]">
                        Included · not swappable
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-14 border-t border-[#E5CDBB] pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="eyebrow">Configured Total</div>
              <div className="font-serif text-3xl sm:text-4xl mt-1">{formatUsd(total)}</div>
              {isCustomized && (
                <div className="text-xs text-[#6B5757] mt-1">
                  {swaps.length} swap{swaps.length > 1 ? "s" : ""} · {total > product.price ? "+" : ""}{formatUsd(total - product.price)} vs base
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={openShare}
                disabled={savingShare}
                className="btn-outline inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Share2 className="w-4 h-4" /> Save & Share
              </button>
              <button
                onClick={() => {
                  addItem(product, 1, { variants, price: total, swaps });
                  toast.success(isCustomized ? "Customized system added" : "Added to cart");
                }}
                data-testid="product-configurator-add"
                className="btn-primary inline-flex items-center gap-3"
              >
                Add to Cart <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section data-testid={PRODUCT.timeline} className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-16">
            <div className="eyebrow">Your Recovery Journey</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4">
              Your {product.duration_weeks}-Week Timeline
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-2 bottom-2 w-px bg-[#E5CDBB]" aria-hidden />
            <div className="space-y-12">
              {timeline.map((t, i) => (
                <div key={t.stage} data-testid={PRODUCT.timelineStep(i)} className="relative pl-16">
                  <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-[#2A1F1F] text-[#FDF3ED] flex items-center justify-center font-mono text-xs">
                    {i === 0 ? "★" : `0${i}`}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#B85C56]">{t.stage}</div>
                  <h3 className="font-serif text-2xl sm:text-3xl mt-2">{t.title}</h3>
                  <p className="text-sm text-[#6B5757] mt-3 max-w-lg leading-relaxed">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDF3ED] py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <div className="eyebrow">Answers</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible>
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#E5CDBB]">
                <AccordionTrigger data-testid={PRODUCT.faqTrigger(i)} className="font-serif text-lg text-left hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6B5757] pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-12 text-xs text-[#A08D8D] text-center leading-relaxed">
            <strong>Medical Disclaimer:</strong> ALTA Recovery products are aftercare accessories. They are not medical devices and do not diagnose, treat or cure any condition. Always follow your surgeon's instructions.
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#F0DFD0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <p className="font-serif text-2xl">Not sure which system is right for you?</p>
          <Link to="/finder" className="btn-outline inline-block mt-6">Open the Procedure Finder</Link>
        </div>
      </section>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md bg-[#FBF6F1] border-[#E5CDBB]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Share your configuration</DialogTitle>
            <DialogDescription className="text-[#6B5757] text-sm">
              Anyone with this link can review your exact system — including every brand swap and the configured total.
              They'll see it as {formatUsd(total)}{isCustomized && ` with ${swaps.length} swap${swaps.length > 1 ? "s" : ""}`}.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-2">
            <input
              data-testid={PRODUCT.shareLinkInput}
              value={shareUrl}
              readOnly
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-[#E5CDBB]"
            />
            <button
              data-testid={PRODUCT.copyLink}
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A1F1F] text-[#FEFBF9] text-xs uppercase tracking-widest hover:bg-[#B85C56] transition-colors"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <div className="mt-6 text-[11px] text-[#A08D8D]">
            Configurations are saved for 30 days and never expire while the recipient's cart is open.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
